import { RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { StyleFadeDirection } from '../helpers/style-helper';

export type ScrollState = {
    startReached: boolean;
    endReached: boolean;
};

type UseScrollStateProps = {
    scrollContainerRef?: RefObject<HTMLElement | null>;
    initialState?: ScrollState;
    orientation?: 'horizontal' | 'vertical';
    refreshTrigger?: object | string | number | boolean;
};

export default function useScrollState(props: UseScrollStateProps) {
    const { scrollContainerRef, initialState, refreshTrigger, orientation = 'horizontal' } = props;

    const [scrollState, setScrollState] = useState<ScrollState>(
        initialState || {
            startReached: true,
            endReached: true
        }
    );

    const onScroll = useCallback(() => {
        if (scrollContainerRef?.current) {
            const startReached =
                orientation === 'horizontal'
                    ? scrollContainerRef.current.scrollLeft <= 0
                    : scrollContainerRef.current.scrollTop <= 0;
            const endReached =
                orientation === 'horizontal'
                    ? Math.ceil(scrollContainerRef.current.offsetWidth + scrollContainerRef.current.scrollLeft) >=
                      scrollContainerRef.current.scrollWidth
                    : Math.ceil(scrollContainerRef.current.offsetHeight + scrollContainerRef.current.scrollTop) >=
                      scrollContainerRef.current.scrollHeight;
            setScrollState({
                startReached: startReached,
                endReached: endReached
            });
        }
    }, [scrollContainerRef, orientation]);

    useEffect(() => {
        const scrollContainer = scrollContainerRef?.current;
        scrollContainer?.addEventListener('scroll', onScroll);
        return () => {
            scrollContainer?.removeEventListener('scroll', onScroll);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scrollContainerRef?.current]);

    // Appended content (e.g. lazily paginated items) grows scrollWidth without firing a scroll
    // event, leaving endReached stale; a ResizeObserver alone misses this because the container's
    // own box does not change.
    useEffect(() => {
        const scrollContainer = scrollContainerRef?.current;
        if (!scrollContainer) {
            return;
        }
        const mutationObserver = new MutationObserver(onScroll);
        mutationObserver.observe(scrollContainer, { childList: true });
        const resizeObserver = new ResizeObserver(onScroll);
        resizeObserver.observe(scrollContainer);
        return () => {
            mutationObserver.disconnect();
            resizeObserver.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scrollContainerRef?.current, onScroll]);

    const { inView, ref } = useInView({ threshold: 0.01 });

    useEffect(() => {
        ref(scrollContainerRef?.current);
    }, [scrollContainerRef?.current]);

    useEffect(() => {
        let timeout: ReturnType<typeof setTimeout> | undefined;
        if (inView) {
            timeout = setTimeout(() => {
                onScroll();
            }, 50);
        }
        return () => {
            clearTimeout(timeout);
        };
    }, [onScroll, JSON.stringify(refreshTrigger), inView]);

    const styleFadedState = useMemo(() => {
        const defaultStyle = orientation === 'horizontal' ? StyleFadeDirection.Horizontal : StyleFadeDirection.Vertical;
        const startReachedStyle = orientation === 'horizontal' ? StyleFadeDirection.Right : StyleFadeDirection.Bottom;
        const endReachedStyle = orientation === 'horizontal' ? StyleFadeDirection.Left : StyleFadeDirection.Top;

        if (scrollState.endReached && scrollState.startReached) {
            return StyleFadeDirection.None;
        }

        return scrollState.startReached ? startReachedStyle : scrollState.endReached ? endReachedStyle : defaultStyle;
    }, [scrollState.startReached, scrollState.endReached, orientation]);

    return {
        scrollState,
        styleFadedState
    };
}
